// Helpers for ranks — the admin-managed entities that replaced the hardcoded
// "pharmd"/"spep"/"both" strings.
//
// A rank declares how its curricula match preceptors to rotations:
//   matchBy === 'experience'  -> matched by speciality  (preceptor.specialityIds)
//   matchBy === 'institution' -> matched by institution (preceptor.institutionIds)
//
// It also carries scheduling defaults: a rotation count and, per rotation, a
// year-less "MM-DD" date range that gets resolved to concrete dates when loaded
// into the curriculum form (see resolveRankRotationDefaults).

export const MATCH_BY_EXPERIENCE = 'experience';
export const MATCH_BY_INSTITUTION = 'institution';

export const MATCH_BY_OPTIONS = [
    {
        value: MATCH_BY_EXPERIENCE,
        label: 'Experience',
        description: 'Rotations are matched to preceptors by experience (speciality)',
    },
    {
        value: MATCH_BY_INSTITUTION,
        label: 'Institution',
        description: 'Rotations are matched to preceptors by institution',
    },
];

const MATCH_BY_META = {
    [MATCH_BY_EXPERIENCE]: {
        label: 'Experience',
        pluralLabel: 'Experiences',
        preceptorField: 'specialityIds',
        legacyPreceptorField: 'specialityId',
    },
    [MATCH_BY_INSTITUTION]: {
        label: 'Institution',
        pluralLabel: 'Institutions',
        preceptorField: 'institutionIds',
        legacyPreceptorField: 'institutionId',
    },
};

/** Normalize a matchBy value, defaulting to experience matching. */
export const normalizeMatchBy = (matchBy) => (
    matchBy === MATCH_BY_INSTITUTION ? MATCH_BY_INSTITUTION : MATCH_BY_EXPERIENCE
);

/** Labels and preceptor field names for a matchBy value. */
export const getMatchByMeta = (matchBy) => MATCH_BY_META[normalizeMatchBy(matchBy)];

/** Find a rank by id, falling back to a case-insensitive name match. */
export const findRank = (ranks, rankRef) => {
    if (!rankRef || !Array.isArray(ranks)) return null;
    const ref = String(rankRef);
    return (
        ranks.find((rank) => String(rank._id) === ref)
        || ranks.find((rank) => (rank.name || '').toLowerCase() === ref.toLowerCase())
        || null
    );
};

/** The rank a curriculum belongs to, tolerating legacy rank-name-only records. */
export const getCurriculumRank = (ranks, curriculum) => (
    findRank(ranks, curriculum?.rankId) || findRank(ranks, curriculum?.rank)
);

/** How a curriculum matches preceptors; defaults to experience when unknown. */
export const getCurriculumMatchBy = (ranks, curriculum) => (
    normalizeMatchBy(getCurriculumRank(ranks, curriculum)?.matchBy)
);

/**
 * Entity ids a preceptor covers for a given matchBy, with legacy single-value fallback.
 */
export const getPreceptorEntityIds = (preceptor, matchBy) => {
    const meta = getMatchByMeta(matchBy);
    const ids = preceptor?.[meta.preceptorField];
    if (Array.isArray(ids) && ids.length > 0) {
        return ids.map(String).filter(Boolean);
    }
    const legacy = preceptor?.[meta.legacyPreceptorField];
    return legacy ? [String(legacy)] : [];
};

/**
 * Rank ids a preceptor serves. Falls back to the legacy single `rank` string,
 * where "both" meant "eligible for every rank".
 */
export const getPreceptorRankIds = (preceptor, ranks = []) => {
    const rankIds = preceptor?.rankIds;
    if (Array.isArray(rankIds) && rankIds.length > 0) {
        return rankIds.map(String).filter(Boolean);
    }
    const legacy = (preceptor?.rank || '').trim();
    if (!legacy) return [];
    if (legacy.toLowerCase() === 'both') return ranks.map((rank) => String(rank._id));
    const match = findRank(ranks, legacy);
    return match ? [String(match._id)] : [];
};

/**
 * Display string for every rank a preceptor holds, e.g. "PharmD, SPEP".
 * Falls back to the legacy `rank` string (denormalized by the backend to the
 * primary rank's name) when the full rank list isn't available yet.
 */
export const formatPreceptorRanks = (preceptor, ranks = []) => {
    const rankIds = getPreceptorRankIds(preceptor, ranks);
    if (rankIds.length > 0 && ranks.length > 0) {
        const names = rankIds
            .map((id) => findRank(ranks, id)?.name)
            .filter(Boolean);
        if (names.length > 0) return names.join(', ');
    }
    return preceptor?.rank || 'N/A';
};

/** Whether a preceptor is eligible for a curriculum of the given rank. */
export const preceptorServesRank = (preceptor, rankId, ranks = []) => {
    if (!rankId) return true;
    return getPreceptorRankIds(preceptor, ranks).includes(String(rankId));
};

const pad2 = (value) => String(value).padStart(2, '0');

/** Normalize a year-less date to canonical 'MM-DD'; returns '' when unparseable. */
export const toMonthDay = (value) => {
    if (!value) return '';
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return `${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
    }

    const parts = String(value).trim().replace(/\//g, '-').split('-');
    // A full date was supplied (YYYY-MM-DD) — keep month/day only.
    const [monthRaw, dayRaw] = parts.length >= 3 && parts[0].length === 4
        ? [parts[1], parts[2]]
        : parts;

    const month = parseInt(monthRaw, 10);
    const day = parseInt(dayRaw, 10);
    if (!month || !day || month < 1 || month > 12 || day < 1 || day > 31) return '';
    return `${pad2(month)}-${pad2(day)}`;
};

/** Render 'MM-DD' for display, e.g. '09-01' -> '1 Sep'. */
export const formatMonthDay = (monthDay) => {
    const normalized = toMonthDay(monthDay);
    if (!normalized) return '—';
    const month = parseInt(normalized.slice(0, 2), 10);
    const day = parseInt(normalized.slice(3), 10);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${monthNames[month - 1]}`;
};

const buildDate = (monthDay, year) => {
    const month = parseInt(monthDay.slice(0, 2), 10);
    let day = parseInt(monthDay.slice(3), 10);
    // Clamp 29 Feb down to 28 Feb in non-leap years.
    if (month === 2 && day === 29) {
        const isLeap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
        if (!isLeap) day = 28;
    }
    return new Date(year, month - 1, day);
};

const toISODate = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/**
 * Turn a rank's year-less rotation defaults into concrete ISO dates.
 *
 * The first date is anchored to its closest upcoming occurrence — this year if
 * that MM-DD has not passed yet, otherwise next year. Every later date then
 * walks forward monotonically, rolling into the following year whenever it
 * would otherwise land before the date preceding it, so a schedule that
 * straddles New Year (Sep -> Jun) resolves across two years rather than
 * collapsing backwards.
 *
 * Mirrors rank_utils.resolve_default_rotations on the backend.
 */
export const resolveRankRotationDefaults = (defaultRotations, referenceDate = new Date()) => {
    const today = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
    let previous = null;

    return (defaultRotations || []).map((rotation) => {
        const entry = { startDate: '', endDate: '' };

        ['startDate', 'endDate'].forEach((key) => {
            const monthDay = toMonthDay(rotation?.[key]);
            if (!monthDay) return;

            let candidate;
            if (previous === null) {
                candidate = buildDate(monthDay, today.getFullYear());
                if (candidate < today) candidate = buildDate(monthDay, today.getFullYear() + 1);
            } else {
                candidate = buildDate(monthDay, previous.getFullYear());
                if (candidate < previous) candidate = buildDate(monthDay, previous.getFullYear() + 1);
            }

            entry[key] = toISODate(candidate);
            previous = candidate;
        });

        return entry;
    });
};

/**
 * Curriculum-form defaults for a rank: rotation count, resolved rotation date
 * ranges, and the overall span they cover.
 */
export const buildCurriculumDefaultsFromRank = (rank, referenceDate = new Date()) => {
    const rotationsCount = Math.max(1, parseInt(rank?.defaultRotationsCount, 10) || 1);
    const stored = Array.isArray(rank?.defaultRotations) ? rank.defaultRotations : [];

    // Pad/truncate so the list always matches the declared count.
    const sized = Array.from({ length: rotationsCount }, (_, i) => stored[i] || { startDate: '', endDate: '' });
    const rotations = resolveRankRotationDefaults(sized, referenceDate);
    const dated = rotations.filter((r) => r.startDate && r.endDate);

    return {
        rotationsCount,
        rotations,
        startDate: dated.length ? dated[0].startDate : '',
        endDate: dated.length ? dated[dated.length - 1].endDate : '',
        hasDates: dated.length > 0,
    };
};
